import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AdminAuth } from '@/lib/api';
import { useSeo } from '@/lib/seo';
import { TEST_IDS } from '@/constants/testIds/floreal';
import AppointmentsPanel from '@/components/admin/AppointmentsPanel';
import EventsPanel from '@/components/admin/EventsPanel';
import RegistrationsPanel from '@/components/admin/RegistrationsPanel';
import GalleryPanel from '@/components/admin/GalleryPanel';
import MessagesPanel from '@/components/admin/MessagesPanel';
import FeaturedPanel from '@/components/admin/FeaturedPanel';
import SiteSettingsPanel from '@/components/admin/SiteSettingsPanel';
import SiteTextsPanel from '@/components/admin/SiteTextsPanel';
import SiteMenuPanel from '@/components/admin/SiteMenuPanel';
import SiteSeoPanel from '@/components/admin/SiteSeoPanel';
import MatrimoniPanel from '@/components/admin/MatrimoniPanel';
import WorkshopEmailPanel from '@/components/admin/WorkshopEmailPanel';

export default function AdminDashboard() {
    useSeo({ title: 'Floreal — Pannello admin' });
    const navigate = useNavigate();
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        AdminAuth.check().then((ok) => {
            if (cancelled) return;
            if (ok) setReady(true);
            else navigate('/admin', { replace: true });
        });
        return () => { cancelled = true; };
    }, [navigate]);

    const onLogout = async () => {
        await AdminAuth.logout();
        navigate('/admin', { replace: true });
    };

    if (!ready) {
        return (
            <div className="min-h-screen flex items-center justify-center text-muted-foreground">Verifica accesso…</div>
        );
    }

    return (
        <div data-testid={TEST_IDS.adminDashboard} className="min-h-screen bg-atelier-cream">
            <header className="border-b border-border bg-background/85 backdrop-blur sticky top-0 z-30">
                <div className="container-atelier h-16 flex items-center justify-between">
                    <div className="flex items-baseline gap-3">
                        <span className="font-serif text-2xl tracking-[0.18em] uppercase font-medium">Floreal</span>
                        <span className="hidden sm:inline-block eyebrow text-muted-foreground tracking-[0.18em]">Pannello amministratore</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button asChild variant="ghost" size="sm"><a href="/" target="_blank" rel="noreferrer">Vai al sito</a></Button>
                        <Button onClick={onLogout} variant="outline" size="sm">
                            <LogOut className="h-4 w-4 mr-2" /> Esci
                        </Button>
                    </div>
                </div>
            </header>

            <main className="container-atelier py-8 sm:py-10">
                <h1 className="font-serif text-3xl sm:text-4xl tracking-tight">Gestione atelier</h1>
                <p className="text-sm text-muted-foreground mt-2 mb-8">Appuntamenti, eventi, iscrizioni e galleria.</p>
                <Tabs defaultValue="settings" className="w-full">
                    <TabsList data-testid={TEST_IDS.adminTabs} className="w-full sm:w-auto inline-flex flex-wrap h-auto">
                        <TabsTrigger value="settings" data-testid="admin-tab-settings">Impostazioni sito</TabsTrigger>
                        <TabsTrigger value="texts" data-testid="admin-tab-texts">Contenuti</TabsTrigger>
                        <TabsTrigger value="menu" data-testid="admin-tab-menu">Menu</TabsTrigger>
                        <TabsTrigger value="seo" data-testid="admin-tab-seo">SEO</TabsTrigger>
                        <TabsTrigger value="featured" data-testid="admin-tab-featured">Immagini evidenza</TabsTrigger>
                        <TabsTrigger value="gallery" data-testid="admin-tab-gallery">Galleria</TabsTrigger>
                        <TabsTrigger value="events" data-testid="admin-tab-events">Eventi</TabsTrigger>
                        <TabsTrigger value="appointments" data-testid="admin-tab-appointments">Appuntamenti</TabsTrigger>
                        <TabsTrigger value="matrimoni" data-testid="admin-tab-matrimoni">Matrimoni</TabsTrigger>
                        <TabsTrigger value="registrations" data-testid="admin-tab-registrations">Prenotazioni Workshop</TabsTrigger>
                        <TabsTrigger value="emailws" data-testid="admin-tab-email">Email workshop</TabsTrigger>
                        <TabsTrigger value="messages" data-testid="admin-tab-messages">Messaggi</TabsTrigger>
                    </TabsList>
                    <TabsContent value="settings" className="mt-6"><SiteSettingsPanel /></TabsContent>
                    <TabsContent value="texts" className="mt-6"><SiteTextsPanel /></TabsContent>
                    <TabsContent value="menu" className="mt-6"><SiteMenuPanel /></TabsContent>
                    <TabsContent value="seo" className="mt-6"><SiteSeoPanel /></TabsContent>
                    <TabsContent value="featured" className="mt-6"><FeaturedPanel /></TabsContent>
                    <TabsContent value="gallery" className="mt-6"><GalleryPanel /></TabsContent>
                    <TabsContent value="events" className="mt-6"><EventsPanel /></TabsContent>
                    <TabsContent value="appointments" className="mt-6"><AppointmentsPanel /></TabsContent>
                    <TabsContent value="matrimoni" className="mt-6"><MatrimoniPanel /></TabsContent>
                    <TabsContent value="registrations" className="mt-6"><RegistrationsPanel /></TabsContent>
                    <TabsContent value="emailws" className="mt-6"><WorkshopEmailPanel /></TabsContent>
                    <TabsContent value="messages" className="mt-6"><MessagesPanel /></TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
